import { supabase } from "@/integrations/supabase/client";

export interface AdminExamSet {
  id: string;
  examSetId: string;
  certificationId: string;
  name: string;
  version?: string;
  type: "official" | "custom";
  questionCount: number;
  timeLimitMinutes: number;
  status: "active" | "draft";
  questionIds: string[];
  order?: number;
}

type Listener = () => void;
let _items: AdminExamSet[] = [];
let _loaded = false;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((fn) => fn());

export function rowToExamSet(r: any): AdminExamSet {
  return {
    id: r.exam_set_id,
    examSetId: r.exam_set_id,
    certificationId: r.certification_id,
    name: r.name,
    version: r.version ?? undefined,
    type: r.type as "official" | "custom",
    questionCount: r.question_count,
    timeLimitMinutes: r.time_limit_minutes,
    status: r.status as "active" | "draft",
    questionIds: [...new Set((r.question_ids as string[]) ?? [])],
    order: r.sort_order ?? 0,
  };
}

async function loadFromDb() {
  // The `question_ids` column is no longer readable directly by clients (it is
  // revoked at the database level for `authenticated` / `anon` to prevent
  // learners from pre-reading the question list for an upcoming exam). We
  // fetch the rest of the columns here and hydrate question_ids via a secure
  // SECURITY DEFINER RPC that is role/tier-gated and audit-logs learner reads.
  const { data } = await supabase
    .from("exam_sets")
    .select(
      "exam_set_id, certification_id, name, version, type, question_count, time_limit_minutes, status, sort_order",
    )
    .order("sort_order");

  const baseRows = (data ?? []).map((r: any) => ({ ...r, question_ids: [] as string[] }));

  // The RPC is admin-gated; for learners it returns an empty set. We swallow
  // errors so that learners (or unit tests that don't mock `rpc`) still get
  // a usable store populated from the table columns.
  try {
    const { data: idRows } = await supabase.rpc("get_exam_set_question_ids_batch");
    if (Array.isArray(idRows)) {
      const idMap = new Map<string, string[]>();
      for (const row of idRows as Array<{ exam_set_id: string; question_ids: unknown }>) {
        idMap.set(row.exam_set_id, Array.isArray(row.question_ids) ? (row.question_ids as string[]) : []);
      }
      for (const r of baseRows) {
        const ids = idMap.get(r.exam_set_id);
        if (ids) r.question_ids = ids;
      }
    }
  } catch {
    // ignore — learner path or test environment without rpc mocking
  }

  _items = baseRows.map(rowToExamSet);
  _loaded = true;
  notify();
}

loadFromDb();

export const examSetStore = {
  get: () => _items,
  subscribe: (fn: Listener) => { listeners.add(fn); return () => listeners.delete(fn); },
  isLoaded: () => _loaded,
  reload: loadFromDb,

  async add(item: AdminExamSet) {
    if (item.order === undefined) {
      const siblings = _items.filter((s) => s.certificationId === item.certificationId);
      item.order = siblings.length > 0 ? Math.max(...siblings.map((s) => s.order ?? 0)) + 1 : 1;
    }
    const { error } = await supabase.from("exam_sets").insert({
      exam_set_id: item.examSetId,
      certification_id: item.certificationId,
      name: item.name,
      version: item.version ?? null,
      type: item.type,
      question_count: item.questionCount,
      time_limit_minutes: item.timeLimitMinutes,
      status: item.status,
      question_ids: item.questionIds as unknown as null,
      sort_order: item.order,
    });
    if (!error) {
      _items = [..._items, item];
      notify();
    }
  },

  async update(examSetId: string, patch: Partial<AdminExamSet>) {
    if (patch.questionIds) {
      patch.questionIds = [...new Set(patch.questionIds)];
    }
    const dbPatch: Record<string, unknown> = {};
    if (patch.name !== undefined) dbPatch.name = patch.name;
    if (patch.version !== undefined) dbPatch.version = patch.version;
    if (patch.type !== undefined) dbPatch.type = patch.type;
    if (patch.questionCount !== undefined) dbPatch.question_count = patch.questionCount;
    if (patch.timeLimitMinutes !== undefined) dbPatch.time_limit_minutes = patch.timeLimitMinutes;
    if (patch.status !== undefined) dbPatch.status = patch.status;
    if (patch.questionIds !== undefined) dbPatch.question_ids = patch.questionIds;
    if (patch.order !== undefined) dbPatch.sort_order = patch.order;

    const { error } = await supabase.from("exam_sets").update(dbPatch).eq("exam_set_id", examSetId);
    if (!error) {
      _items = _items.map((s) => (s.examSetId === examSetId ? { ...s, ...patch } : s));
      notify();
    }
  },

  async remove(examSetId: string) {
    const { error } = await supabase.from("exam_sets").delete().eq("exam_set_id", examSetId);
    if (!error) {
      _items = _items.filter((s) => s.examSetId !== examSetId);
      notify();
    }
  },

  async reorderByIds(certificationId: string, orderedIds: string[]) {
    const siblings = _items.filter((s) => s.certificationId === certificationId);
    if (siblings.length !== orderedIds.length) return;
    const sorted = [...siblings].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const orders = sorted.map((s, i) => s.order ?? i + 1);
    const idToOrder = new Map(orderedIds.map((id, i) => [id, orders[i]]));
    _items = _items.map((s) => {
      const newOrder = idToOrder.get(s.examSetId);
      return newOrder !== undefined ? { ...s, order: newOrder } : s;
    });
    notify();
    for (const [esId, order] of idToOrder) {
      await supabase.from("exam_sets").update({ sort_order: order }).eq("exam_set_id", esId);
    }
  },
};
